import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import SidebarLinkGroup from "./SidebarLinkGroup";
import { useSelector } from "react-redux";
import CanView from "../components/CanView.jsx";
import { FaCrown, FaHeadset, FaCircleCheck } from "react-icons/fa6";
import { PrimaryButton } from "../components/Buttons.jsx";
import { useTheme } from "@/hooks/useTheme.js";
import { App as CapacitorApp } from "@capacitor/app";

function Sidebar({ sidebarOpen, setSidebarOpen, sidebarExpanded, setSidebarExpanded }) {
  const { brandTheme } = useTheme();
  const navigate = useNavigate();

  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef(null);
  const sidebar = useRef(null);


  const { unseenOrderCount } = useSelector((state) => state.orders);
  const { dashboardActionCounts } = useSelector((state) => state.app);
  const unseenIDScans = dashboardActionCounts?.idScanReview ?? 0;
  const prospectSellerCount = dashboardActionCounts?.prospect ?? 0;
  const [appVersion, setAppVersion] = useState(null);
  const { selectedLocation, selectedMerchant, user } = useSelector(
    (state) => state.app
  );
  useEffect(() => {
    CapacitorApp.getInfo()
      .then((info) => {
        setAppVersion(`${info.version} ${info.build ? `(${info.build})` : ""}`);
      })
      .catch(() => {});
  }, []);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  if (
    selectedMerchant?.status === "suspended" &&
    user.type !== "saucey_admin"
  ) {
    return null;
  }
  return (
    <div className="min-w-fit">
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`app-sidebar fixed inset-0 bg-slate-900 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`app-sidebar flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-[100dvh] overflow-y-scroll lg:overflow-y-auto no-scrollbar w-72 lg:w-20 lg:sidebar-expanded:!w-72 2xl:!w-72 shrink-0 bg-theme-primary p-4 transition-all duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-72"
        }`}
      >
        {/* Sidebar header */}
        <div className="flex justify-between mb-2 2xl:mb-10 pr-3 sm:px-2">
          {/* Close button */}
          <button
            ref={trigger}
            className="lg:hidden text-slate-500 hover:text-slate-400"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg
              className="w-6 h-6 fill-current"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          {/* Logo */}
          <NavLink end to="/" className="block">
            {brandTheme === "direct" ? (
              <img
                src={
                  sidebarExpanded
                    ? "https://cdn.saucey.com/direct/web/saucey-direct-logo_lightbg.svg"
                    : "https://cdn.saucey.com/direct/web/SD-small.svg"
                }
                alt="Logo"
                className="h-10"
              />
            ) : (
              <img
                src={
                  sidebarExpanded
                    ? "https://cdn.saucey.com/logo/wordmark-light.svg"
                    : "https://cdn.saucey.com/logo/Saucey Pin Logo Black.jpg"
                }
                alt="Logo"
                className={sidebarExpanded ? "h-5 mt-2" : "h-8 mt-2"}
              />
            )}
          </NavLink>
        </div>

        {/* Links */}
        <div className="space-y-8 __tutorial-sidebar-links">
          {/* Pages group */}
          <div>
            <ul className="mt-3">
              {(selectedLocation?.prospect || selectedLocation?.unpublished) && (
                <div className="w-full mb-2">
                  <PrimaryButton
                    width="full"
                    onClick={() => {
                      navigate("/onboarding");
                    }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <FaCircleCheck />
                      <span className="lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                        Finish Setup
                      </span>
                    </div>
                  </PrimaryButton>
                </div>
              )}
              {selectedLocation && selectedLocation.direct === false && selectedLocation.onboarding === true && selectedLocation.onboarding_step !== "under_review" && (
                <div className="w-full mb-2">
                  <PrimaryButton
                    width="full"
                    onClick={() => {
                      navigate("/marketplace-onboarding");
                    }}
                  >
                    <div className="flex items-center gap-2 w-full relative">
                      <FaCircleCheck />
                      <span className="lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                        Onboarding
                      </span>
                      {(() => {
                        const stepIdx = { legal: 1, stripe: 2, licenses: 3, menu: 4 };
                        const completed = stepIdx[selectedLocation?.onboarding_step] || 0;
                        const remaining = 4 - completed;
                        if (remaining <= 0) return null;
                        return (
                          <span className="ml-auto bg-amber-500 text-white text-xs font-semibold rounded-full w-5 h-5 text-center leading-5">
                            {remaining}
                          </span>
                        );
                      })()}
                    </div>
                  </PrimaryButton>
                </div>
              )}
              {selectedLocation && selectedLocation.direct === false && selectedLocation.onboarding === true && selectedLocation.onboarding_step === "under_review" && (
                <div className="w-full mb-2">
                  <div
                    className="w-full px-3 py-2 rounded bg-slate-700 text-slate-200 text-sm flex items-center gap-2 cursor-default"
                    title="Your store is under review by Saucey"
                  >
                    <FaCircleCheck className="text-emerald-400" />
                    <span className="lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                      Under review
                    </span>
                  </div>
                </div>
              )}
              {selectedLocation?.cartwheel_team_id && (
                <div className="w-full mb-2">
                  <PrimaryButton
                    width="full"
                    onClick={() => {
                      window.open(
                        "https://saucey.cartwheel.tech/app/#login?redirectTo=dispatcher-map"
                      );
                    }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <FaHeadset />
                      Launch Dispatch
                    </div>
                  </PrimaryButton>
                </div>
              )}
              <CanView directOnly>
                {!selectedMerchant?.prospect && <li
                  className={`px-3 py- lg:px-3 lg:py-2 rounded-sm mb-0.5 last:mb-0 ${
                    pathname === "/" && "bg-slate-900"
                  }`}
                >
                  <NavLink
                    end
                    to="/"
                    className={`block py-2 lg:py-1 text-slate-200 truncate transition duration-150 ${
                      pathname === "/"
                        ? "hover:text-slate-200"
                        : "hover:text-white"
                    }`}
                  >
                    <div className="flex items-center">
                      <svg
                        className="shrink-0 h-6 w-6 lg:h-6 lg:w-6 h-8 w-8 hidden lg:block"
                        viewBox="0 0 24 24"
                      >
                        <path
                          className={`fill-current ${
                            pathname === "/" || pathname.includes("dashboard")
                              ? "text-theme-text-primary"
                              : "text-slate-400"
                          }`}
                          d="M12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0z"
                        />
                        <path
                          className={`fill-current ${
                            pathname === "/" || pathname.includes("dashboard")
                              ? "text-indigo-600"
                              : "text-slate-600"
                          }`}
                          d="M12 3c-4.963 0-9 4.037-9 9s4.037 9 9 9 9-4.037 9-9-4.037-9-9-9z"
                        />
                        <path
                          className={`fill-current ${
                            pathname === "/" || pathname.includes("dashboard")
                              ? "text-indigo-200"
                              : "text-slate-400"
                          }`}
                          d="M12 15c-1.654 0-3-1.346-3-3 0-.462.113-.894.3-1.285L6 6l4.714 3.301A2.973 2.973 0 0112 9c1.654 0 3 1.346 3 3s-1.346 3-3 3z"
                        />
                      </svg>
                      <span className="text-lg lg:text-md font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                        Dashboard
                      </span>
                    </div>
                  </NavLink>
                </li>}
              </CanView>

{!selectedLocation?.prospect && <CanView notProspect>
                <SidebarLinkGroup
                  activecondition={pathname.includes("orders")}
                  exclusiveGroup="main-sidebar-nav"
                  groupId="orders"
                >
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <a
                          href="#0"
                          className={`__tutorial-orders-sidebar-links block text-slate-200 truncate transition duration-150 ${
                            pathname.includes("orders")
                              ? "hover:text-slate-200"
                              : "hover:text-white"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleClick();
                            setSidebarExpanded(true);
                          }}
                        >
                          <div className="flex items-center justify-between relative">
                            <div className="flex items-center">
                              <svg
                                className="shrink-0 h-6 w-6 lg:h-6 lg:w-6 h-8 w-8 hidden lg:block"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  className={`fill-current ${
                                    pathname.includes("orders")
                                      ? "text-indigo-300"
                                      : "text-slate-400"
                                  }`}
                                  d="M13 6.068a6.035 6.035 0 0 1 4.932 4.933H24c-.486-5.846-5.154-10.515-11-11v6.067Z"
                                />
                                <path
                                  className={`fill-current ${
                                    pathname.includes("orders")
                                      ? "text-theme-text-primary"
                                      : "text-slate-700"
                                  }`}
                                  d="M18.007 13c-.474 2.833-2.919 5-5.864 5a5.888 5.888 0 0 1-3.694-1.304L4 20.731C6.131 22.752 8.992 24 12.143 24c6.232 0 11.35-4.851 11.857-11h-5.993Z"
                                />
                                <path
                                  className={`fill-current ${
                                    pathname.includes("orders")
                                      ? "text-indigo-600"
                                      : "text-slate-600"
                                  }`}
                                  d="M6.939 15.007A5.861 5.861 0 0 1 6 11.829c0-2.937 2.167-5.376 5-5.85V0C4.85.507 0 5.614 0 11.83c0 2.695.922 5.174 2.456 7.17l4.483-3.993Z"
                                />
                              </svg>
                              {!open && unseenOrderCount > 0 && (
                                <span className="absolute top-[10px] lg:top-[8px] left-28 -mt-1 -mr-1 bg-red-500 text-white text-xs font-semibold rounded-full  w-5 h-5 text-center leading-5">
                                  {unseenOrderCount}
                                </span>
                              )}
                              <span className="text-sm lg:text-lg font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                Orders
                              </span>
                            </div>
                            {/* Icon */}
                            <div className="flex shrink-0 ml-2">
                              <svg
                                className={`hidden lg:block w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${
                                  open && "rotate-180"
                                }`}
                                viewBox="0 0 12 12"
                              >
                                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                              </svg>
                            </div>
                          </div>
                        </a>
                        <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                          <ul
                            className={`pl-0 lg:pl-9 mt-1 ${!open && "hidden"}`}
                          >
                            <li className="mb-1 last:mb-0 relative">
                              <NavLink
                                to="/orders"
                                className={({ isActive }) =>
                                  "block py-0.5 lg:py-0 transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Active Orders
                                </span>
                                {unseenOrderCount > 0 && (
                                  <span className="absolute top-[10px] lg:top-[8px] right-1 -mt-1 -mr-1 bg-red-500 text-white text-xs font-semibold rounded-full  w-5 h-5 text-center leading-5">
                                    {unseenOrderCount}
                                  </span>
                                )}
                              </NavLink>
                            </li>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                to="/past-orders"
                                className={({ isActive }) =>
                                  "block py-0.5 lg:py-0 transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Past Orders
                                </span>
                              </NavLink>
                            </li>
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
              </CanView>}
              

              <CanView>
                <SidebarLinkGroup
                  activecondition={pathname.includes("catalog")}
                  exclusiveGroup="main-sidebar-nav"
                  groupId="catalog"
                >
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <a
                          href="#0"
                          className={`__tutorial-catalog-sidebar-links block text-slate-200 truncate transition duration-150 ${
                            pathname.includes("catalog")
                              ? "hover:text-slate-200"
                              : "hover:text-white"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleClick();
                            setSidebarExpanded(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <svg
                                className="shrink-0 h-6 w-6 lg:h-6 lg:w-6 h-8 w-8 hidden lg:block"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  className={`fill-current ${
                                    pathname.includes("catalog")
                                      ? "text-indigo-300"
                                      : "text-slate-400"
                                  }`}
                                  d="M13 15l11-7L11.504.136a1 1 0 00-1.019.007L0 7l13 8z"
                                />
                                <path
                                  className={`fill-current ${
                                    pathname.includes("catalog")
                                      ? "text-indigo-600"
                                      : "text-slate-700"
                                  }`}
                                  d="M13 15L0 7v9c0 .355.189.685.496.864L13 24v-9z"
                                />
                                <path
                                  className={`fill-current ${
                                    pathname.includes("catalog")
                                      ? "text-theme-text-primary"
                                      : "text-slate-600"
                                  }`}
                                  d="M13 15.047V24l10.573-7.181A.999.999 0 0024 16V8l-11 7.047z"
                                />
                              </svg>
                              <span className="text-sm lg:text-lg font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                Catalog
                              </span>
                            </div>
                            {/* Icon */}
                            <div className="flex shrink-0 ml-2">
                              <svg
                                className={`hidden lg:block w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${
                                  open && "rotate-180"
                                }`}
                                viewBox="0 0 12 12"
                              >
                                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                              </svg>
                            </div>
                          </div>
                        </a>
                        <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                          <ul
                            className={`pl-0 lg:pl-9 mt-1 ${!open && "hidden"}`}
                          >
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/catalog/products"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Products
                                </span>
                              </NavLink>
                            </li>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/catalog/categories"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Categories
                                </span>
                              </NavLink>
                            </li>
                            <CanView permissions={"saucey_admin"}>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/external-feeds"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    External Feed
                                  </span>
                                </NavLink>
                              </li>
                            </CanView>
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
              </CanView>

              {!selectedLocation?.prospect && (<CanView directOnly>

                <SidebarLinkGroup
                  activecondition={
                    pathname.startsWith("/crm/") ||
                    pathname.includes("customers")
                  }
                  exclusiveGroup="main-sidebar-nav"
                  groupId="customers"
                >
                  {(handleClick, open) => (
                    <React.Fragment>
                      <a
                        href="#0"
                        className={`block text-slate-200 truncate transition duration-150 ${
                          pathname.startsWith("/crm/") ||
                          pathname.includes("customers")
                            ? "hover:text-slate-200"
                            : "hover:text-white"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick();
                          setSidebarExpanded(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg
                              className="shrink-0 h-6 w-6 lg:h-6 lg:w-6 h-8 w-8 hidden lg:block"
                              viewBox="0 0 24 24"
                            >
                              <path
                                className={`fill-current ${
                                  pathname.startsWith("/crm/") ||
                                  pathname.includes("customers")
                                    ? "text-theme-text-primary"
                                    : "text-slate-600"
                                }`}
                                d="M18.974 8H22a2 2 0 012 2v6h-2v5a1 1 0 01-1 1h-2a1 1 0 01-1-1v-5h-2v-6a2 2 0 012-2h.974zM20 7a2 2 0 11-.001-3.999A2 2 0 0120 7zM2.974 8H6a2 2 0 012 2v6H6v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5H0v-6a2 2 0 012-2h.974zM4 7a2 2 0 11-.001-3.999A2 2 0 014 7z"
                              />
                              <path
                                className={`fill-current ${
                                  pathname.startsWith("/crm/") ||
                                  pathname.includes("customers")
                                    ? "text-indigo-300"
                                    : "text-slate-400"
                                }`}
                                d="M12 6a3 3 0 110-6 3 3 0 010 6zm2 18h-4a1 1 0 01-1-1v-6H6v-6a3 3 0 013-3h6a3 3 0 013 3v6h-3v6a1 1 0 01-1 1z"
                              />
                            </svg>
                            <span className="text-sm lg:text-lg font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                              Customers
                            </span>
                          </div>
                          {/* Icon */}
                          <div className="flex shrink-0 ml-2">
                            <svg
                              className={`hidden lg:block w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${
                                open && "rotate-180"
                              }`}
                              viewBox="0 0 12 12"
                            >
                              <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                            </svg>
                          </div>
                        </div>
                      </a>
                      <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                        <ul
                          className={`pl-0 lg:pl-9 mt-1 ${!open && "hidden"}`}
                        >
                          <li className="mb-1 last:mb-0">
                            <NavLink
                              end
                              to="/crm/people"
                              className={({ isActive }) =>
                                "block transition duration-150 truncate " +
                                (isActive || pathname.includes("people")
                                  ? "text-theme-text-primary"
                                  : "text-theme-text-muted hover:text-theme-text-secondary")
                              }
                            >
                              <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                People
                              </span>
                            </NavLink>
                          </li>
                          <CanView permissions={"manager"} addons={["crm"]}>
                            <>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/crm/campaigns"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive || pathname.includes("campaigns")
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Campaigns
                                  </span>
                                </NavLink>
                              </li>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/crm/broadcasts"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive || pathname.includes("broadcasts")
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Broadcasts
                                  </span>
                                </NavLink>
                              </li>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/crm/settings"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Settings
                                  </span>
                                </NavLink>
                              </li>
                            </>
                          </CanView>
                        </ul>
                      </div>
                    </React.Fragment>
                  )}
                </SidebarLinkGroup>
              </CanView>)}
              {!selectedLocation?.prospect && (<CanView directOnly permissions={"manager"}><li
                  className={`px-3 py- lg:px-3 lg:py-2 rounded-sm mb-0.5 last:mb-0 ${
                    pathname.includes("reports") && "bg-slate-900"
                  }`}
                >
                  <NavLink
                    end
                    to="/reports"
                    className={`block py-2 lg:py-1 text-slate-200 truncate transition duration-150 ${
                      pathname.includes("reports")
                        ? "hover:text-slate-200"
                        : "hover:text-white"
                    }`}
                  >
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="icon icon-tabler icons-tabler-outline icon-tabler-clipboard-data lg:w-6 lg:h-6 w-8 h-8 hidden lg:block"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-indigo-300"
                              : "text-slate-400"
                          }`}
                          d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"
                        />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-indigo-300"
                              : "text-slate-400"
                          }`}
                          d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"
                        />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-indigo-300"
                              : "text-slate-400"
                          }`}
                          d="M9 17v-4"
                        />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-indigo-300"
                              : "text-slate-400"
                          }`}
                          d="M12 17v-1"
                        />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-theme-text-primary"
                              : "text-slate-600"
                          }`}
                          d="M15 17v-2"
                        />
                        <path
                          className={`stroke-current ${
                            pathname.includes("reports")
                              ? "text-indigo-600"
                              : "text-slate-700"
                          }`}
                          d="M12 17v-1"
                        />
                      </svg>
                      <span className="text-lg lg:text-md font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                        Reports
                      </span>
                    </div>
                  </NavLink>
                </li></CanView>)}

              {/* Settings */}
              <SidebarLinkGroup
                activecondition={pathname.startsWith("/settings")}
                exclusiveGroup="main-sidebar-nav"
                groupId="settings"
              >
                {(handleClick, open) => {
                  return (
                    <React.Fragment>
                      <a
                        href="#0"
                        className={`block text-slate-200 truncate transition duration-150 ${
                          pathname.includes("settings")
                            ? "hover:text-slate-200"
                            : "hover:text-white"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleClick();
                          setSidebarExpanded(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg
                              className="shrink-0 h-6 w-6 lg:h-6 lg:w-6 h-8 w-8 hidden lg:block"
                              viewBox="0 0 24 24"
                            >
                              <path
                                className={`fill-current ${
                                  pathname.includes("settings")
                                    ? "text-theme-text-primary"
                                    : "text-slate-600"
                                }`}
                                d="M19.714 14.7l-7.007 7.007-1.414-1.414 7.007-7.007c-.195-.4-.298-.84-.3-1.286a3 3 0 113 3 2.969 2.969 0 01-1.286-.3z"
                              />
                              <path
                                className={`fill-current ${
                                  pathname.includes("settings")
                                    ? "text-indigo-300"
                                    : "text-slate-400"
                                }`}
                                d="M10.714 18.3c.4-.195.84-.298 1.286-.3a3 3 0 11-3 3c.002-.446.105-.885.3-1.286l-6.007-6.007 1.414-1.414 6.007 6.007z"
                              />
                              <path
                                className={`fill-current ${
                                  pathname.includes("settings")
                                    ? "text-theme-text-primary"
                                    : "text-slate-600"
                                }`}
                                d="M5.7 10.714c.195.4.298.84.3 1.286a3 3 0 11-3-3c.446.002.885.105 1.286.3l7.007-7.007 1.414 1.414L5.7 10.714z"
                              />
                              <path
                                className={`fill-current ${
                                  pathname.includes("settings")
                                    ? "text-indigo-300"
                                    : "text-slate-400"
                                }`}
                                d="M19.707 9.292a3.012 3.012 0 00-1.415 1.415L13.286 5.7c-.4.195-.84.298-1.286.3a3 3 0 113-3 2.969 2.969 0 01-.3 1.286l5.007 5.006z"
                              />
                            </svg>
                            <span className="text-sm lg:text-lg font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                              Settings
                            </span>
                          </div>
                          {/* Icon */}
                          <div className="flex shrink-0 ml-2">
                            <svg
                              className={`hidden lg:block w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${
                                open && "rotate-180"
                              }`}
                              viewBox="0 0 12 12"
                            >
                              <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                            </svg>
                          </div>
                        </div>
                      </a>
                      <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                        <ul
                          className={`pl-0 lg:pl-9 mt-1 ${!open && "hidden"}`}
                        >
                          <li className="mb-1 last:mb-0">
                            <NavLink
                              end
                              to="/settings/account"
                              className={({ isActive }) =>
                                "block py-0.5 lg:py-0 transition duration-150 truncate " +
                                (isActive
                                  ? "text-theme-text-primary"
                                  : "text-theme-text-muted hover:text-theme-text-secondary")
                              }
                            >
                              <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                My Account
                              </span>
                            </NavLink>
                          </li>

                          <CanView directOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                to="/settings/store"
                                className={() => {
                                  const isStoreRoute = pathname === "/settings/store" || 
                                                       pathname === "/settings/store/appearance" || 
                                                       pathname === "/settings/store/hours" ||
                                                       pathname === "/settings/hours";
                                  return "block transition duration-150 truncate " +
                                    (isStoreRoute
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary");
                                }}
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  My Store
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView directOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/business"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  My Business
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView mpOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/commissions"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  My Commissions
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView mpOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/licenses"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Licenses
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          {selectedMerchant?.stripe_cust_id && (
                            <CanView permissions={"admin"}>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/settings/billing"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Billing
                                  </span>
                                </NavLink>
                              </li>
                            </CanView>
                          )}
                          {selectedMerchant?.locations?.length > 1 && (
                            <CanView permissions={"admin"} directOnly>
                              <li className="mb-1 last:mb-0">
                                <NavLink
                                  end
                                  to="/settings/mothership"
                                  className={({ isActive }) =>
                                    "block transition duration-150 truncate " +
                                    (isActive
                                      ? "text-theme-text-primary"
                                      : "text-theme-text-muted hover:text-theme-text-secondary")
                                  }
                                >
                                  <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                    Mothership
                                  </span>
                                </NavLink>
                              </li>
                            </CanView>
                          )}

                          <CanView permissions={"admin"} directOnly>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/customization"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Customization
                                </span>
                              </NavLink>
                            </li>
                          </CanView>

                          <CanView permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/users"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Users
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView directOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/notifications"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  My Notifications
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView mpOnly notProspect>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/notifications"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  My Notifications
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView directOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/marketing"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Marketing Materials
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                          <CanView directOnly permissions={"manager"}>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/fraud-management"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Fraud Management
                                </span>
                              </NavLink>
                            </li>
                          </CanView>

                          <CanView permissions={"admin"} directOnly>
                            <li className="mb-1 last:mb-0">
                              <NavLink
                                end
                                to="/settings/finance"
                                className={({ isActive }) =>
                                  "block transition duration-150 truncate " +
                                  (isActive
                                    ? "text-theme-text-primary"
                                    : "text-theme-text-muted hover:text-theme-text-secondary")
                                }
                              >
                                <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                  Payouts
                                </span>
                              </NavLink>
                            </li>
                          </CanView>
                        </ul>
                      </div>
                    </React.Fragment>
                  );
                }}
              </SidebarLinkGroup>
            </ul>
          </div>
        </div>

        {/* Expand / collapse button */}
        <div className=" flex w-full flex-1 items-end">
          <CanView permissions={"saucey_admin"}>
            <SidebarLinkGroup
              activecondition={pathname.includes("admin")}
              upsideDown
              exclusiveGroup="main-sidebar-nav"
              groupId="admin"
            >
              {(handleClick, open) => {
                return (
                  <React.Fragment>
                    <a
                      href="#0"
                      className={`block text-slate-200 truncate transition duration-150 ${
                        pathname.includes("admin")
                          ? "hover:text-slate-200"
                          : "hover:text-white"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClick();
                        setSidebarExpanded(true);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center relative">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`icon icon-tabler icon-tabler-world-cog stroke-current lg:w-6 lg:h-6 w-8 h-8 hidden lg:block ${
                              pathname.includes("admin")
                                ? "text-indigo-300"
                                : "text-slate-400"
                            }`}
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M21 12a9 9 0 1 0 -8.979 9" />
                            <path d="M3.6 9h16.8" />
                            <path d="M3.6 15h8.9" />
                            <path d="M11.5 3a17 17 0 0 0 0 18" />
                            <path d="M12.5 3a16.992 16.992 0 0 1 2.522 10.376" />
                            <path d="M19.001 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                            <path d="M19.001 15.5v1.5" />
                            <path d="M19.001 21v1.5" />
                            <path d="M22.032 17.25l-1.299 .75" />
                            <path d="M17.27 20l-1.3 .75" />
                            <path d="M15.97 17.25l1.3 .75" />
                            <path d="M20.733 20l1.3 .75" />
                          </svg>
                          <span className="text-lg lg:text-md font-semibold lg:font-medium ml-0 lg:ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                            Admin <FaCrown />
                          </span>
                          {!open &&
                            (unseenIDScans > 0 || prospectSellerCount > 0) && (
                              <span
                                className="absolute top-[11px] lg:top-[9px] left-[8.5rem] flex items-center gap-1"
                                role="img"
                                aria-label={
                                  [
                                    unseenIDScans > 0
                                      ? `${unseenIDScans} ID scan reviews pending`
                                      : null,
                                    prospectSellerCount > 0
                                      ? `${prospectSellerCount} prospect sellers`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")
                                }
                              >
                                {unseenIDScans > 0 && (
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-500 ring-2 ring-slate-800"
                                    title="Pending ID scan reviews"
                                  />
                                )}
                                {prospectSellerCount > 0 && (
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400 ring-2 ring-slate-800"
                                    title="Prospect sellers"
                                  />
                                )}
                              </span>
                            )}
                        </div>
                        {/* Icon */}
                        <div className="flex shrink-0 ml-2">
                          <svg
                            className={`hidden lg:block w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${
                              !open && "rotate-180"
                            }`}
                            viewBox="0 0 12 12"
                          >
                            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                          </svg>
                        </div>
                      </div>
                    </a>
                    <div className="lg:hidden lg:sidebar-expanded:block 2xl:block mb-2">
                      <ul className={`pl-0 mt-1 ${!open && "hidden"}`}>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/merchants"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Merchant Accounts <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0 relative">
                          <NavLink
                            end
                            to="/admin/sellers"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            {prospectSellerCount > 0 && (
                              <span className="absolute top-[10px] lg:top-[8px] right-1 -mt-1 -mr-1 bg-amber-500 text-white text-xs font-semibold rounded-full  w-5 h-5 text-center leading-5">
                                {prospectSellerCount}
                              </span>
                            )}
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Stores <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/marketplace-commissions"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Commissions<FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            to="/admin/compliance/tax-rules"
                            className={
                              "block transition duration-150 truncate " +
                              (pathname.includes("/admin/compliance")
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Compliance<FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/admins"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Admin Users <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/review-custom-products"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Review custom products<FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/state-restrictions"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              State Restrictions <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/admin/menu-cards"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Menu Cards <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0">
                          <NavLink
                            end
                            to="/catalog/master-menu"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Master Menu Management <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                        <li className="mb-1 last:mb-0 relative">
                          <NavLink
                            end
                            to="/id-scans"
                            className={({ isActive }) =>
                              "block transition duration-150 truncate " +
                              (isActive
                                ? "text-theme-text-primary"
                                : "text-theme-text-muted hover:text-theme-text-secondary")
                            }
                          >
                            {unseenIDScans > 0 && (
                              <span className="absolute top-[10px] lg:top-[8px] right-1 -mt-1 -mr-1 bg-rose-500 text-white text-xs font-semibold rounded-full  w-5 h-5 text-center leading-5">
                                {unseenIDScans}
                              </span>
                            )}
                            <span className="text-lg lg:text-md font-semibold lg:font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200 flex items-center gap-1">
                              Review ID Scans <FaCrown />
                            </span>
                          </NavLink>
                        </li>
                      </ul>
                    </div>
                  </React.Fragment>
                );
              }}
            </SidebarLinkGroup>
          </CanView>
        </div>

        <div className="pt-3 hidden lg:inline-flex 2xl:hidden justify-end mt-auto __tutorial-sidebar-collapse-button">
          <div className="px-3 py-3 lg:px-3 lg:py-2">
            <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg
                className="w-6 h-6 fill-current sidebar-expanded:rotate-180"
                viewBox="0 0 24 24"
              >
                <path
                  className="text-slate-400"
                  d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z"
                />
                <path className="text-slate-600" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>
        {appVersion && (
          <p className="text-white opacity-20 text-xs mb-2 text-right">
            v{appVersion}
          </p>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
